import React, { useState, useEffect } from 'react';
import { Users, Edit2, Trash2, Shield, Plus, Mail, UserX, UserCheck, KeyRound, Clock, Lock, Unlock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { userService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AccessHistoryDialog from '../components/users/AccessHistoryDialog';

const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Accesso completo a tutti i moduli' },
  { value: 'quality', label: 'Qualità', description: 'Gestione piani di controllo, registrazione controlli, gestione NC' },
  { value: 'production', label: 'Produzione', description: 'Gestione lotti, aggiornamento avanzamento lotti' },
  { value: 'operator', label: 'Operatore', description: 'Accesso limitato, aggiornamento fasi lotto assegnate' },
  { value: 'management', label: 'Direzione', description: 'Cruscotti e report in sola lettura' },
  { value: 'commerciale', label: 'Commerciale', description: 'Anagrafica clienti, offerte e ordini commerciali' },
];

const DEPARTMENTS = ['Management', 'Produzione', 'Qualita', 'Ufficio Tecnico', 'Logistica', 'Manutenzione', 'Commerciale'];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [historyUser, setHistoryUser] = useState(null);
  const { user: currentUser, hasRole } = useAuth();

  const [editForm, setEditForm] = useState({
    first_name: '', last_name: '', role: '', department: '', real_email: '', is_active: true
  });

  const [createForm, setCreateForm] = useState({
    first_name: '', last_name: '', email: '', password: '', role: 'operator', department: '', real_email: ''
  });

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const response = await userService.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      department: user.department || '',
      real_email: user.real_email || '',
      is_active: user.is_active
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await userService.update(editingUser.id, editForm);
      toast.success('Utente aggiornato');
      setEditDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...createForm };
      if (!payload.real_email) delete payload.real_email;
      if (!payload.department) delete payload.department;
      await userService.create(payload);
      toast.success('Utente creato con successo');
      setCreateDialogOpen(false);
      setCreateForm({ first_name: '', last_name: '', email: '', password: '', role: 'operator', department: '', real_email: '' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser.id) {
      toast.error('Non puoi eliminare il tuo account');
      return;
    }
    const choice = window.confirm(
      `Vuoi ELIMINARE DEFINITIVAMENTE l'utente ${user.first_name} ${user.last_name}?\n\nPremi OK per eliminare definitivamente.\nPremi Annulla per annullare.`
    );
    if (choice) {
      try {
        await userService.deletePermanent(user.id);
        toast.success('Utente eliminato definitivamente');
        loadUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
      }
    }
  };

  const handleDeactivate = async (user) => {
    if (user.id === currentUser.id) {
      toast.error('Non puoi disattivare il tuo account');
      return;
    }
    if (window.confirm(`Disattivare ${user.first_name} ${user.last_name}?`)) {
      try {
        await userService.delete(user.id);
        toast.success('Utente disattivato');
        loadUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore nella disattivazione');
      }
    }
  };

  const handleReactivate = async (user) => {
    try {
      await userService.update(user.id, { is_active: true });
      toast.success('Utente riattivato');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella riattivazione');
    }
  };

  const handleResetPassword = async (user) => {
    const pwd = window.prompt(`Inserisci la nuova password per ${user.first_name} ${user.last_name} (min 4 caratteri):`);
    if (!pwd) return;
    if (pwd.length < 4) { toast.error('Password troppo corta (min 4 caratteri)'); return; }
    try {
      await userService.resetPassword(user.id, pwd);
      toast.success('Password aggiornata');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset password');
    }
  };

  const handleUnlock = async (user) => {
    try {
      await userService.unlock(user.id);
      toast.success('Account sbloccato');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello sblocco');
    }
  };

  const isLockedNow = (user) => {
    if (!user?.lockout_until) return false;
    try { return new Date(user.lockout_until).getTime() > Date.now(); } catch { return false; }
  };

  const lockoutMinutesLeft = (user) => {
    if (!isLockedNow(user)) return 0;
    return Math.max(1, Math.ceil((new Date(user.lockout_until).getTime() - Date.now()) / 60000));
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-rose-100 text-rose-700',
      quality: 'bg-blue-100 text-blue-700',
      production: 'bg-amber-100 text-amber-700',
      operator: 'bg-slate-100 text-slate-700',
      management: 'bg-purple-100 text-purple-700',
      commerciale: 'bg-teal-100 text-teal-700',
    };
    return styles[role] || styles.operator;
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestione Utenti</h1>
          <p className="text-sm text-slate-500 mt-1">Gestisci account e permessi utente</p>
        </div>
        {hasRole('admin') && (
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-user-btn">
            <Plus className="w-4 h-4 mr-2" />Nuovo Utente
          </Button>
        )}
      </div>

      {/* Role Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Permessi Ruoli
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ROLES.map((role) => (
              <div key={role.value} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <Badge className={getRoleBadge(role.value)}>{role.label}</Badge>
                <p className="text-sm text-slate-600">{role.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessun utente trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email Login</th>
                    <th>Ruolo</th>
                    <th>Reparto</th>
                    <th>Stato</th>
                    <th>Creato il</th>
                    <th>Ultimo accesso</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`}>
                      <td className="font-medium">
                        {user.first_name} {user.last_name}
                        {user.id === currentUser.id && (
                          <Badge className="ml-2 bg-blue-100 text-blue-700">Tu</Badge>
                        )}
                        {user.real_email && (
                          <div className="mt-0.5">
                            <a href={`mailto:${user.real_email}`} className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1">
                              <Mail className="w-3 h-3" />{user.real_email}
                            </a>
                          </div>
                        )}
                      </td>
                      <td className="text-sm text-slate-600 font-mono">{user.email}</td>
                      <td>
                        <Badge className={getRoleBadge(user.role)}>
                          {ROLES.find(r => r.value === user.role)?.label || user.role}
                        </Badge>
                      </td>
                      <td className="text-sm">{user.department || '-'}</td>
                      <td>
                        <div className="flex flex-col gap-1 items-start">
                          <Badge className={user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}>
                            {user.is_active ? 'Attivo' : 'Disattivato'}
                          </Badge>
                          {isLockedNow(user) && (
                            <Badge className="bg-red-100 text-red-800 border border-red-300 text-[10px]" data-testid={`locked-badge-${user.id}`}>
                              <Lock className="w-3 h-3 mr-1 inline" /> Bloccato · {lockoutMinutesLeft(user)}m
                            </Badge>
                          )}
                          {!isLockedNow(user) && (user.failed_attempts || 0) > 0 && (
                            <span className="text-[10px] text-amber-600">
                              {user.failed_attempts} tentativi falliti
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-xs text-slate-500 font-mono">{fmtDate(user.created_at)}</td>
                      <td className="text-xs text-slate-500 font-mono">{fmtDate(user.last_login)}</td>
                      <td>
                        {hasRole('admin') && (
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setHistoryUser(user)} data-testid={`history-user-${user.id}`} title="Storico accessi">
                              <Clock className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.id}`} title="Modifica utente">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleResetPassword(user)} data-testid={`reset-pwd-${user.id}`} title="Reset password">
                              <KeyRound className="w-4 h-4 text-blue-500" />
                            </Button>
                            {isLockedNow(user) && (
                              <Button variant="ghost" size="sm" onClick={() => handleUnlock(user)} data-testid={`unlock-user-${user.id}`} title="Sblocca account">
                                <Unlock className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                            {user.id !== currentUser.id && user.is_active && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeactivate(user)} data-testid={`deactivate-user-${user.id}`} title="Disattiva utente">
                                <UserX className="w-4 h-4 text-amber-500" />
                              </Button>
                            )}
                            {user.id !== currentUser.id && !user.is_active && (
                              <Button variant="ghost" size="sm" onClick={() => handleReactivate(user)} data-testid={`reactivate-user-${user.id}`} title="Riattiva utente">
                                <UserCheck className="w-4 h-4 text-emerald-600" />
                              </Button>
                            )}
                            {user.id !== currentUser.id && (
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} data-testid={`delete-user-${user.id}`} title="Elimina definitivamente">
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Utente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input required value={createForm.first_name} onChange={(e) => setCreateForm(f => ({ ...f, first_name: e.target.value }))} data-testid="create-user-firstname" />
                </div>
                <div className="space-y-2">
                  <Label>Cognome *</Label>
                  <Input required value={createForm.last_name} onChange={(e) => setCreateForm(f => ({ ...f, last_name: e.target.value }))} data-testid="create-user-lastname" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email Login *</Label>
                  <Input type="email" required placeholder="nome@evotech.it" value={createForm.email} onChange={(e) => setCreateForm(f => ({ ...f, email: e.target.value }))} data-testid="create-user-email" />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input type="password" required minLength={4} value={createForm.password} onChange={(e) => setCreateForm(f => ({ ...f, password: e.target.value }))} data-testid="create-user-password" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email Reale</Label>
                <Input type="email" placeholder="nome.cognome@email.com" value={createForm.real_email} onChange={(e) => setCreateForm(f => ({ ...f, real_email: e.target.value }))} data-testid="create-user-real-email" />
                <p className="text-xs text-slate-500">Indirizzo email personale o aziendale dell&apos;utente</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ruolo *</Label>
                  <Select value={createForm.role} onValueChange={(v) => setCreateForm(f => ({ ...f, role: v }))}>
                    <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Reparto</Label>
                  <Select value={createForm.department || "none"} onValueChange={(v) => setCreateForm(f => ({ ...f, department: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="create-user-department"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Nessuno --</SelectItem>
                      {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="create-user-submit">Crea Utente</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Utente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input required value={editForm.first_name} onChange={(e) => setEditForm(f => ({ ...f, first_name: e.target.value }))} data-testid="edit-user-firstname" />
                </div>
                <div className="space-y-2">
                  <Label>Cognome</Label>
                  <Input required value={editForm.last_name} onChange={(e) => setEditForm(f => ({ ...f, last_name: e.target.value }))} data-testid="edit-user-lastname" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email Reale</Label>
                <Input type="email" placeholder="nome.cognome@email.com" value={editForm.real_email} onChange={(e) => setEditForm(f => ({ ...f, real_email: e.target.value }))} data-testid="edit-user-real-email" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ruolo</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm(f => ({ ...f, role: v }))}>
                    <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Reparto</Label>
                  <Select value={editForm.department || "none"} onValueChange={(v) => setEditForm(f => ({ ...f, department: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="edit-user-department"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Nessuno --</SelectItem>
                      {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Stato</Label>
                <Select
                  value={editForm.is_active ? 'attivo' : 'disattivato'}
                  onValueChange={(v) => setEditForm(f => ({ ...f, is_active: v === 'attivo' }))}
                  disabled={editingUser?.id === currentUser?.id}
                >
                  <SelectTrigger data-testid="edit-user-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attivo">Attivo</SelectItem>
                    <SelectItem value="disattivato">Disattivato</SelectItem>
                  </SelectContent>
                </Select>
                {editingUser?.id === currentUser?.id && (
                  <p className="text-xs text-slate-400">Non puoi modificare lo stato del tuo account.</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="edit-user-submit">Aggiorna</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AccessHistoryDialog
        open={!!historyUser}
        onOpenChange={(o) => !o && setHistoryUser(null)}
        user={historyUser}
      />
    </div>
  );
}
